import PrintableTableContentRow from "../../../../components/utils/printable-table-generator/PrintableTableContentRow";
import PrintableTableFooterRow from "../../../../components/utils/printable-table-generator/PrintableTableFooterRow";
import PrintableTableHeaderRow from "../../../../components/utils/printable-table-generator/PrintableTableHeaderRow";
import { Dropdown } from "primereact/dropdown";
import { Button } from "primereact/button";

// builds a compact page list with ellipses, e.g. [1, 2, 3, 4, "...", 20]
const getPageItems = (currentPage, totalPages, windowSize = 4) => {
  if (totalPages <= 1) return [1];

  let start = Math.max(1, currentPage - Math.floor((windowSize - 1) / 2));
  let end = Math.min(start + windowSize - 1, totalPages);
  if (end - start + 1 < windowSize) {
    start = Math.max(1, end - windowSize + 1);
  }

  const items = [];

  if (start > 1) {
    items.push(1);
    if (start > 2) items.push("...");
  }

  for (let page = start; page <= end; page++) items.push(page);

  if (end < totalPages) {
    if (end < totalPages - 1) items.push("...");
    items.push(totalPages);
  }

  return items;
};

const SimplePaginator = ({ first, rows, totalRecords, rowsPerPageOptions, onPageChange }) => {
  const totalPages = Math.max(1, Math.ceil(totalRecords / rows));
  const currentPage = Math.floor(first / rows) + 1;
  const pageItems = getPageItems(currentPage, totalPages);

  const goToPage = (page) => {
    const clamped = Math.min(Math.max(page, 1), totalPages);
    onPageChange({ first: (clamped - 1) * rows, rows });
  };

  return (
    <div className="row-center-center gap-1">
      <Button
        type="button"
        icon="pi pi-angle-double-left"
        className="p-button-text p-button-rounded"
        disabled={currentPage === 1}
        onClick={() => goToPage(1)}
      />
      <Button
        type="button"
        icon="pi pi-angle-left"
        className="p-button-text p-button-rounded"
        disabled={currentPage === 1}
        onClick={() => goToPage(currentPage - 1)}
      />

      <div className="row-center-center gap-1">
        {pageItems.map((item, idx) =>
          item === "..." ? (
            <span key={`ellipsis-${idx}`} className="px-1 text-slate-400">
              ...
            </span>
          ) : (
            <Button
              type="button"
              key={item}
              label={String(item)}
              className="p-button-text p-button-rounded"
              style={
                item === currentPage
                  ? { backgroundColor: "#eef2ff", color: "#4338ca" }
                  : undefined
              }
              onClick={() => goToPage(item)}
            />
          )
        )}
      </div>

      <Button
        type="button"
        icon="pi pi-angle-right"
        className="p-button-text p-button-rounded"
        disabled={currentPage === totalPages}
        onClick={() => goToPage(currentPage + 1)}
      />
      <Button
        type="button"
        icon="pi pi-angle-double-right"
        className="p-button-text p-button-rounded"
        disabled={currentPage === totalPages}
        onClick={() => goToPage(totalPages)}
      />

      <Dropdown
        className="ml-2"
        value={rows}
        options={rowsPerPageOptions.map((opt) => ({ label: `${opt}`, value: opt }))}
        onChange={(e) => onPageChange({ first: 0, rows: e.value })}
      />
    </div>
  );
};

const settings = [
  { label: "NO", field: "no", width: "w-[3rem]", layout: "row-center-start" },
  { label: "BC NAME", field: "bc_name", width: "w-[15rem]", layout: "row-start-start", wrap: true },
  { label: "CODE", field: "customer_code", width: "w-[7.5rem]", layout: "row-start-start" },
  { label: "NAME", field: "customer_name", width: "w-[22.5rem]", layout: "row-start-start", wrap: true },
  { label: "QTY BUY", field: "total_quantity_buy", width: "w-[8rem]", layout: "row-end-start", type: "number" },
  { label: "QTY FREE", field: "total_quantity_free", width: "w-[8rem]", layout: "row-end-start", type: "number" },
  { label: "QTY PAKAI", field: "total_quantity_pemakaian", width: "w-[8rem]", layout: "row-end-start", type: "number" },
  { label: "QTY SISA", field: "total_quantity_sisa", width: "w-[8rem]", layout: "row-end-start", type: "number" },
];

const footerSettings = [
  { field: "label", width: "w-[48rem]", layout: "row-end-start", colSpan: 4 },
  { field: "total_quantity_buy", width: "w-[8rem]", layout: "row-end-start", type: "number" },
  { field: "total_quantity_free", width: "w-[8rem]", layout: "row-end-start", type: "number" },
  { field: "total_quantity_pemakaian", width: "w-[8rem]", layout: "row-end-start", type: "number" },
  { field: "total_quantity_sisa", width: "w-[8rem]", layout: "row-end-start", type: "number" },
];

const ReportTemplateGroup = (props) => {
  const { list, title, pagination, totals } = props;

  const rows = list.map((item, idx) => ({ ...item, no: item.no ?? idx + 1 }));

  const totalBuy =
    totals?.total_quantity_buy ?? rows.reduce((sum, row) => sum + Number(row?.total_quantity_buy || 0), 0);
  const totalFree =
    totals?.total_quantity_free ?? rows.reduce((sum, row) => sum + Number(row?.total_quantity_free || 0), 0);
  const totalPemakaian =
    totals?.total_quantity_pemakaian ?? rows.reduce((sum, row) => sum + Number(row?.total_quantity_pemakaian || 0), 0);
  const totalSisa =
    totals?.total_quantity_sisa ?? rows.reduce((sum, row) => sum + Number(row?.total_quantity_sisa || 0), 0);
  const totalLabel = totals?.label ?? "TOTAL";

  return (
    <>
      <table>
        {/* locks each column to a fixed width shared by every row, so the
            content row cells stay aligned with the header row's individually-widthed cells */}
        <colgroup>
          {settings.map((setting, idx) => (
            <col key={idx} className={setting.width} />
          ))}
        </colgroup>

        <PrintableTableHeaderRow settings={settings} title={title} />

        <tbody>
          {rows.map((row, idx) => (
            <PrintableTableContentRow
              key={row.customer_id ? `${row.customer_id}-${idx}` : idx}
              settings={settings}
              data={row}
            />
          ))}
        </tbody>

        <PrintableTableFooterRow
          settings={footerSettings}
          data={{ label: totalLabel, total_quantity_buy: totalBuy, total_quantity_free: totalFree, total_quantity_pemakaian: totalPemakaian, total_quantity_sisa: totalSisa }}
        />
      </table>

      {pagination && (
        <div className="print:hidden row-center-center w-full pt-2">
          <SimplePaginator
            first={pagination.first}
            rows={pagination.rows}
            totalRecords={pagination.totalRecords}
            rowsPerPageOptions={pagination.rowsPerPageOptions}
            onPageChange={pagination.onPageChange}
          />
        </div>
      )}
    </>
  );
};

export default ReportTemplateGroup;
